// Decimal arithmetic on digit strings, so rounding and scaling a figure never
// meet a float (`1.245` is `1.24499…` as a double, `2.093 * 100` is
// `209.29999999999998`). Shared by the cell-source check (`passageSpelling`,
// #12452, #13498) and the chat-citation cell matcher (`citedTableCells`,
// #12818).

/** A non-negative decimal as its digit strings. */
export interface Decimal {
  int: string;
  frac: string;
}

/** `d` × 10^`by`, by moving the point. */
export const shift = (d: Decimal, by: number): Decimal => {
  let digits = d.int + d.frac;
  let point = d.int.length + by;
  if (point < 0) {
    digits = "0".repeat(-point) + digits;
    point = 0;
  }
  if (point > digits.length) digits = digits.padEnd(point, "0");
  return {
    int: digits.slice(0, point).replace(/^0+(?=\d)/, "") || "0",
    frac: digits.slice(point),
  };
};

/** An unsigned number as a cell stores it, exponent form included. */
export const decimalOf = (text: string): Decimal | null => {
  const parts = text.match(/^(\d+)(?:\.(\d+))?(?:e([-+]?\d+))?$/i);
  if (!parts) return null;
  const [, int, frac = "", exponent = "0"] = parts;
  return shift({ int, frac }, Number(exponent));
};

/** `d` rounded half-up to `places` decimals, as a plain decimal string. */
export const roundHalfUp = (d: Decimal, places: number): string => {
  const kept = d.frac.slice(0, places).padEnd(places, "0");
  const up = (d.frac[places] ?? "0") >= "5";
  const rounded = (BigInt(d.int + kept) + BigInt(up ? 1 : 0))
    .toString()
    .padStart(places + 1, "0");
  return places === 0
    ? rounded
    : `${rounded.slice(0, -places)}.${rounded.slice(-places)}`;
};

/** How many significant digits `d` is written with: leading zeros dropped,
 *  trailing ones kept (`1.00` has three, `0.0055` two). */
export const significantDigits = (d: Decimal): number =>
  (d.int + d.frac).replace(/^0+/, "").length;
